import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@core/services/app/app-api';
import { OrgAndStockroomService } from '@core/services/org-and-stockroom/org-and-stockroom-api';
import { ToastService } from '@core/services/toast-api';
import { MaterialsModule } from '@shared/materials.module';
import { SharedModule } from '@shared/shared.module';

import { NewUserCreationService } from '../new-user-creation.service';
import {
  NEW_USER_ASSIGN_AUTO,
  NEW_USER_ASSIGN_MANUAL,
  NEW_USER_CLONE_SCOPE_ALL_ORGS,
  NEW_USER_CLONE_SCOPE_CURRENT_ORG,
  NEW_USER_USERNAME_MAX,
  NEW_USER_COMMENT_MAX,
} from '../new-user-creation.constant';

interface AssignmentRow {
  userId?: string;
  stockroomId: string;
  stockroomName: string;
  groupId: string;
  groupName: string;
}

@Component({
  selector: 'app-dialog-new-user-creation-form',
  templateUrl: './dialog-new-user-creation-form.html',
  styleUrls: ['./dialog-new-user-creation-form.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialsModule, SharedModule],
})
export class DialogNewUserCreationFormComponent implements OnInit {
  titleText = 'New User Creation';
  subtitleText = 'A Avantor Account gives you access to many of our products with a Org Account.';

  userForm!: FormGroup;
  submitting = false;

  readonly ASSIGN_AUTO = NEW_USER_ASSIGN_AUTO;
  readonly ASSIGN_MANUAL = NEW_USER_ASSIGN_MANUAL;
  readonly CLONE_SCOPE_CURRENT_ORG = NEW_USER_CLONE_SCOPE_CURRENT_ORG;
  readonly CLONE_SCOPE_ALL_ORGS = NEW_USER_CLONE_SCOPE_ALL_ORGS;
  readonly USERNAME_MAX = NEW_USER_USERNAME_MAX;
  readonly COMMENT_MAX = NEW_USER_COMMENT_MAX;

  organizations: any[] = [];
  filteredOrganizations: any[] = [];
  orgFilterText = '';

  existingUsers: any[] = [];
  filteredExistingUsers: any[] = [];
  userFilterText = '';

  stockroomList: any[] = [];
  groupList: any[] = [];

  assignments: AssignmentRow[] = [];

  // username pattern: letters, numbers, spaces, underscore, hyphen, dot — no other specials
  private readonly USERNAME_PATTERN = /^[A-Za-z0-9 _.-]+$/;
  private currentOrgId: string | number | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogNewUserCreationFormComponent>,
    private newUserService: NewUserCreationService,
    private appService: AppService,
    private orgAndStockroomService: OrgAndStockroomService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.bindRequestor();
    this.bindCurrentOrganization();
    this.loadOrganizations();
    this.loadExistingUsers();
    this.loadUserGroups();
  }

  private initForm(): void {
    this.userForm = this.fb.group({
      requestor: [{ value: '', disabled: true }],
      orgId: [null, Validators.required],
      organizationName: [{ value: '', disabled: true }, Validators.required],
      newUsername: ['', [
        Validators.required,
        Validators.maxLength(this.USERNAME_MAX),
        Validators.pattern(this.USERNAME_PATTERN),
      ]],
      newUserEmail: ['', [Validators.required, Validators.email]],
      assignType: [this.ASSIGN_AUTO, Validators.required],
      existingUserId: [null, Validators.required],
      cloneOrganizationScope: [this.CLONE_SCOPE_CURRENT_ORG, Validators.required],
      stockroomId: [null],
      groupId: [null],
      comments: ['', Validators.maxLength(this.COMMENT_MAX)],
    });
  }

  private getRequestorName(user: any): string {
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
      || user.fullName || user.userName || user.loginId || user.email || '';
  }

  private bindRequestor(): void {
    const u = this.appService.getUserData() || {};
    this.userForm.patchValue({ requestor: this.getRequestorName(u) });
    this.userForm.get('requestor')?.disable({ emitEvent: false });
  }

  private bindCurrentOrganization(): void {
    const raw = this.orgAndStockroomService.getStockroomAndOrgName();
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const organization = parsed?.organization || {};
      const orgId = organization.id || organization.orgId || organization.organizationId || null;
      const organizationName = organization.orgName || organization.organizationName || organization.name || '';

      this.currentOrgId = orgId;
      this.userForm.patchValue({
        orgId,
        organizationName,
      }, { emitEvent: false });
    } catch {
      // Leave organization fields empty if the saved org payload is malformed.
    }
  }

  // ---------------------------------------------------------------- orgs ----
  private async loadOrganizations(): Promise<void> {
    try {
      const res = await this.newUserService.getOrganizations();
      this.organizations = this.extractList(res);
      this.filteredOrganizations = [...this.organizations];

      const selectedOrgId = this.currentOrgId || this.userForm.get('orgId')?.value;
      if (selectedOrgId) {
        const matchedOrg = this.organizations.find((org: any) =>
          String(this.getOrgId(org)) === String(selectedOrgId)
        );
        this.userForm.patchValue({
          orgId: selectedOrgId,
          organizationName: this.userForm.get('organizationName')?.value || (matchedOrg ? this.getOrgName(matchedOrg) : ''),
        }, { emitEvent: false });
        if (this.userForm.get('assignType')?.value === this.ASSIGN_MANUAL) {
          await this.loadStockroomsForOrganization(selectedOrgId);
        }
      }
    } catch (err) {
      console.error('[NewUserCreation] loadOrganizations error', err);
    }
  }

  filterOrg(text: string): void {
    this.orgFilterText = text || '';
    const q = this.orgFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }
    this.filteredOrganizations = this.organizations.filter((o: any) => {
      const name = (o.orgName || o.organizationName || o.name || '').toString().toLowerCase();
      return name.includes(q);
    });
  }

  onOrgPanelOpened(opened: boolean): void {
    if (opened) {
      this.orgFilterText = '';
      this.filteredOrganizations = [...this.organizations];
    }
  }

  // ---------------------------------------------------------- existing users
  private async loadExistingUsers(): Promise<void> {
    try {
      const res = await this.newUserService.getActiveUsers();
      this.existingUsers = this.extractList(res);
      this.filteredExistingUsers = [...this.existingUsers];
      if (this.existingUsers.length) {
        const first = this.existingUsers[0];
        const id = this.getUserId(first);
        this.userForm.patchValue({ existingUserId: id });
      }
    } catch (err) {
      console.error('[NewUserCreation] loadExistingUsers error', err);
    }
  }

  filterUsers(text: string): void {
    this.userFilterText = text || '';
    const q = this.userFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredExistingUsers = [...this.existingUsers];
      return;
    }
    this.filteredExistingUsers = this.existingUsers.filter((u: any) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      const id = (u.loginId || u.email || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }

  onUserPanelOpened(opened: boolean): void {
    if (opened) {
      this.userFilterText = '';
      this.filteredExistingUsers = [...this.existingUsers];
    }
  }

  onExistingUserChange(userId: string | number): void {
    if (this.userForm.get('assignType')?.value === this.ASSIGN_MANUAL) {
      this.loadStockroomsForUser(userId);
    }
  }

  onOrgChange(orgId: string | number): void {
    this.assignments = [];
    this.userForm.patchValue({ stockroomId: null, groupId: null });
    if (this.userForm.get('assignType')?.value === this.ASSIGN_MANUAL) {
      this.loadStockroomsForOrganization(orgId);
    }
  }

  private async loadStockroomsForUser(userId: string | number): Promise<void> {
    this.stockroomList = [];
    this.userForm.patchValue({ stockroomId: null });
    if (!this.hasValue(userId)) return;

    try {
      const res = await this.newUserService.getUserStockrooms(userId);
      if (String(this.userForm.get('existingUserId')?.value) !== String(userId)) return;
      this.stockroomList = this.extractList(res);
    } catch (err) {
      console.error('[NewUserCreation] loadStockroomsForUser error', err);
      this.stockroomList = [];
    }
  }

  private async loadStockroomsForOrganization(orgId: string | number): Promise<void> {
    this.stockroomList = [];
    this.userForm.patchValue({ stockroomId: null });
    if (!this.hasValue(orgId)) return;

    try {
      const res = await this.newUserService.getOrganizationStockrooms(orgId);
      if (String(this.userForm.get('orgId')?.value) !== String(orgId)) return;
      this.stockroomList = this.extractList(res);
    } catch (err) {
      console.error('[NewUserCreation] loadStockroomsForOrganization error', err);
      this.stockroomList = [];
    }
  }

  private async loadUserGroups(): Promise<void> {
    try {
      const res = await this.newUserService.getUserGroups();
      this.groupList = this.extractList(res);
    } catch (err) {
      console.error('[NewUserCreation] loadUserGroups error', err);
      this.groupList = [];
    }
  }

  // ------------------------------------------------------------- assign mode
  onAssignTypeChange(value: string): void {
    // Clear table data and dependent selections on radio switch
    this.assignments = [];
    this.userForm.patchValue({ existingUserId: null, stockroomId: null, groupId: null });
    this.setAssignmentValidators(value);
    this.stockroomList = [];
    if (value === this.ASSIGN_AUTO && this.existingUsers.length) {
      const first = this.existingUsers[0];
      const id = this.getUserId(first);
      this.userForm.patchValue({ existingUserId: id });
    }
    if (value === this.ASSIGN_MANUAL) {
      this.loadStockroomsForOrganization(this.userForm.get('orgId')?.value);
    }
  }

  private setAssignmentValidators(assignType: string): void {
    const existingUserCtrl = this.userForm.get('existingUserId');
    const stockroomCtrl = this.userForm.get('stockroomId');
    const groupCtrl = this.userForm.get('groupId');

    if (assignType === this.ASSIGN_AUTO) {
      existingUserCtrl?.setValidators([Validators.required]);
    } else {
      existingUserCtrl?.clearValidators();
    }

    stockroomCtrl?.clearValidators();
    groupCtrl?.clearValidators();
    existingUserCtrl?.updateValueAndValidity({ emitEvent: false });
    stockroomCtrl?.updateValueAndValidity({ emitEvent: false });
    groupCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  // --------------------------------------------------------------- add row
  get canAdd(): boolean {
    const v = this.userForm.getRawValue();
    return v.assignType === this.ASSIGN_MANUAL
      && this.hasValue(v.stockroomId)
      && this.hasValue(v.groupId);
  }

  addAssignment(): void {
    if (!this.canAdd) return;
    const v = this.userForm.getRawValue();

    if (this.isDuplicateAssignment(v.stockroomId, v.groupId)) {
      this.toastService.show('error', 'Duplicate assignment',
        'This stockroom and group combination is already added.', true, 5000);
      return;
    }

    const stockroom = this.stockroomList.find(
      (s: any) => String(this.getStockroomId(s)) === String(v.stockroomId)
    );
    const group = this.groupList.find(
      (g: any) => String(this.getGroupId(g)) === String(v.groupId)
    );

    this.assignments.push({
      stockroomId: v.stockroomId,
      stockroomName: stockroom ? this.getStockroomName(stockroom) : v.stockroomId,
      groupId: v.groupId,
      groupName: group ? this.getGroupName(group) : v.groupId,
    });

    this.userForm.patchValue({ stockroomId: null, groupId: null });
  }

  private isDuplicateAssignment(stockroomId: any, groupId: any): boolean {
    return this.assignments.some(a =>
      String(a.stockroomId) === String(stockroomId)
      && String(a.groupId) === String(groupId)
    );
  }

  removeAssignment(index: number): void {
    if (index < 0 || index >= this.assignments.length) return;
    this.assignments.splice(index, 1);
  }

  // ------------------------------------------------------------------- util
  private extractList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (!res || typeof res !== 'object') return [];
    const keys = [
      'imeorganizationBaseDTO',
      'imeuserBaseDTO',
      'imestockroomBaseDTO',
      'data', 'organizations', 'users', 'userGroups', 'stockrooms', 'items', 'results', 'content',
      'organizationList', 'userList', 'stockroomList', 'list',
    ];
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === 'object') {
        for (const k2 of keys) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }
      }
    }
    return [];
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private getCloneUserAccessOrgId(formValue: any): any {
    return formValue.cloneOrganizationScope === this.CLONE_SCOPE_ALL_ORGS
      ? 0
      : (formValue.orgId || this.currentOrgId);
  }

  private getCloneUserAccessOrgName(formValue: any): string {
    return formValue.cloneOrganizationScope === this.CLONE_SCOPE_ALL_ORGS
      ? 'All'
      : (formValue.organizationName || '');
  }

  getOrgId(org: any): any {
    return org?.id || org?.organizationId || org?.orgId;
  }

  getOrgName(org: any): string {
    return (org?.orgName || org?.organizationName || org?.name || '').toString();
  }

  getUserId(user: any): any {
    return user?.id ?? user?.userId ?? user?.userID ?? user?.loginId;
  }

  getUserDisplayName(user: any): string {
    return user?.loginId || user?.email
      || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      || String(this.getUserId(user) || '');
  }

  getUserEmail(user: any): string {
    return (user?.email || user?.emailAddress || user?.userEmail || '').toString();
  }

  getStockroomId(stockroom: any): any {
    return stockroom?.id ?? stockroom?.stockroomId;
  }

  getStockroomName(stockroom: any): string {
    return stockroom?.stockroomName || stockroom?.name || String(this.getStockroomId(stockroom) ?? '');
  }

  getGroupId(group: any): any {
    return group?.id ?? group?.groupId;
  }

  getGroupName(group: any): string {
    return group?.groupName || group?.name || String(this.getGroupId(group) ?? '');
  }

  isInvalid(controlName: string): boolean {
    const c = this.userForm.get(controlName);
    if (!c) return false;
    return c.invalid && (c.touched || c.dirty);
  }

  hasError(controlName: string, errorKey: string): boolean {
    const c = this.userForm.get(controlName);
    if (!c) return false;
    return !!(c.errors && c.errors[errorKey]) && (c.touched || c.dirty);
  }

  // ---------------------------------------------------------------- submit
  async submit(): Promise<void> {
    this.userForm.markAllAsTouched();

    const isAuto = this.userForm.get('assignType')?.value === this.ASSIGN_AUTO;
    this.setAssignmentValidators(this.userForm.get('assignType')?.value);

    if (!isAuto && !this.assignments.length) {
      this.toastService.show('error', 'Required fields missing',
        'Please add at least one assignment when manual assign is enabled.', true, 5000);
      return;
    }

    if (this.userForm.invalid) {
      this.toastService.show('error', 'Required fields missing',
        'Please fill all required fields and try again.', true, 5000);
      return;
    }

    const v = this.userForm.getRawValue();
    const u = this.appService.getUserData() || {};
    const requestorId = u.userId || u.id || null;
    const clonedUser = this.existingUsers.find((user: any) =>
      String(this.getUserId(user)) === String(v.existingUserId)
    );

    const payload: any = {
      requestorId,
      organizationId: v.orgId,
      newUserName: v.newUsername,
      newUserEmail: v.newUserEmail,
      isAutoAssign: isAuto,
      cloneUserId: isAuto ? v.existingUserId : null,
      cloneUserEmail: isAuto && clonedUser ? this.getUserEmail(clonedUser) : null,
      cloneUserAccessOrgName: isAuto ? this.getCloneUserAccessOrgName(v) : null,
      cloneUserAccessOrgId: isAuto ? this.getCloneUserAccessOrgId(v) : null,
      comments: v.comments || '',
      assignments: !isAuto
        ? this.assignments.map(a => ({
          stockroomId: Number(a.stockroomId),
          stockroomName: a.stockroomName,
          groupId: Number(a.groupId),
          groupName: a.groupName,
        }))
        : [],
    };

    try {
      this.submitting = true;
      const res = await this.newUserService.submitNewUserCreation(payload);
      console.log('[NewUserCreation] submit response:', res);
      this.toastService.show('success', 'New user submitted',
        'New user creation request has been submitted successfully.', true, 5000);
      this.dialogRef.close({ submitted: true, payload });
    } catch (err) {
      console.error('[NewUserCreation] submit error', err);
      this.toastService.show('error', 'Submit failed',
        'Could not submit the new user creation request. Please try again.', true, 5000);
    } finally {
      this.submitting = false;
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
